import React, { useState, useEffect } from 'react';
import {
  ContentLayout,
  Header,
  Table,
  AppLayout,
  SideNavigation,
  Box,
  Button,
  TextFilter,
  Pagination,
  SpaceBetween,
  Container,
  KeyValuePairs,
  Modal,
  FileUpload,
  FormField,
  Select
} from '@cloudscape-design/components';
import { getMeetingsFromDynamoDB, getPresignedUrl, deleteMeeting } from './Api';
import { useAuth } from './AuthContext';
import { Link } from 'react-router-dom';
import MeetingDetails from './MeetingDetails';
import { BreadcrumbGroup } from '@cloudscape-design/components';
import { serviceOverview } from './MeetingStatistics';
import LoadingBar from './LoadingBar';
import { NavigationSide } from './Navigation';

// Values must match the meeting types recognized by get_system_prompt in the
// invokeBedrockAfterTranscribe Lambda, and each needs a matching item in the
// `eloquence-prompts` DynamoDB table.
const MEETING_TYPE_OPTIONS = [
  { label: 'General', value: 'General' },
  { label: 'AB1', value: 'AB1' },
  { label: 'AB2', value: 'AB2' },
  { label: 'AB3', value: 'AB3' },
  { label: 'Demo Day', value: 'DemoDay' },
  { label: 'CheckRide', value: 'CheckRide' },
  { label: 'Discovery', value: 'Discovery' }
];

function MeetingsPage() {
  const { isAuthenticated, logout } = useAuth();

  if (!isAuthenticated) {
    return <div>You need to be authenticated to view this page.</div>;
  }

  console.log(isAuthenticated);
  return (
    <AppLayout
      toolsHide={true}
      navigation={<NavigationSide activeHref="/meetings" />}
      content={
        isAuthenticated ? (
          // <ContentLayout
          //   defaultPadding
          //   headerBackgroundStyle="linear-gradient(135deg, rgb(71, 17, 118) 3%, rgb(131, 57, 157) 44%, rgb(149, 85, 182) 69%, rgb(145, 134, 215) 94%)"
          //   headerVariant="high-contrast"
          //   maxContentWidth={2000}
          //   disableOverlap
          //   header={
          //     <SpaceBetween size="m">
          //       <Header variant="h1" description="View your past meetings">My Meetings</Header>
          //     </SpaceBetween>
          //   }
          // >
          <>
            <MeetingContent />
          </>

        ) : (
          <Button onClick={logout}>Logout</Button>
        )
      }
    />
  );
}

function MeetingContent() {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteringText, setFilteringText] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [uploadFile, setUploadFile] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  // Meeting type drives which rubric invokeBedrockAfterTranscribe grades against.
  // It is embedded in the S3 key so get_system_prompt can recover it downstream.
  const [meetingType, setMeetingType] = useState({ label: 'General', value: 'General' });
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [uploadedFileInfo, setUploadedFileInfo] = useState(null);

  
  // Helper function to check if a meeting has incomplete data
  const isIncompleteData = (meeting) => {
    // A meeting is considered incomplete if it only has userId and uuid
    // or if essential properties like callId, filler, wpm are missing/null/undefined
    const hasBasicIdentifiers = meeting && meeting.userId && meeting.uuid;
    const missingEssentialData = !meeting.callId || 
                                meeting.callId === null || 
                                meeting.callId === undefined ||
                                !meeting.filler || 
                                meeting.filler === null || 
                                meeting.filler === undefined ||
                                !meeting.wpm || 
                                meeting.wpm === null || 
                                meeting.wpm === undefined;
    if (!meeting.weasel || meeting.weasel === null || meeting.weasel === undefined) {
      meeting.weasel = 0;
    }
    // Log for debugging
    if (hasBasicIdentifiers && missingEssentialData) {
      console.log(`Meeting ${meeting.uuid} has incomplete data:`, {
        hasCallId: !!meeting.callId,
        hasFiller: !!meeting.filler,
        hasWpm: !!meeting.wpm,
        hasWeasel: !!meeting.weasel,
      });
    }
    
    return hasBasicIdentifiers && missingEssentialData;
  };

  const validateAndShowConfirm = () => {
    if (!uploadFile.length) {
      setUploadError('Please select a file');
      return;
    }

    // Show confirmation modal
    setShowLegalModal(false);
    setShowConfirmModal(true);
    setShowUploadModal(false);
  };

  const handleLegalConfirm = () => {
    if (uploadedFileInfo) {
    // Add a placeholder meeting with incomplete data
    const newMeeting = {
      uuid: uploadedFileInfo.uuid,
      userId: uploadedFileInfo.userId,
      timestamp: Date.now(),
      callId: uploadFile.length > 0 ? uploadFile[0].name.replace('.mp4', '') : 'Uploaded Meeting'
    };
    
    // Add the new meeting to the list
    setMeetings(prevMeetings => [newMeeting, ...prevMeetings]);
  }
  
  setShowLegalModal(false);
  setShowUploadModal(true);
  setUploadFile([]);
  setUploadError('');
  
  // Refresh meetings list ONLY after user confirms
  fetchMeetings();
  setUploadedFileInfo(null);
  };

  const handleLegalCancel = () => {
    setShowLegalModal(false);
    // if (uploadedFileInfo && uploadedFileInfo.uuid) {
    //   try {
    //     // Call the API to delete the uploaded file
    //     deleteMeeting(uploadedFileInfo.uuid);
    //     console.log("Successfully cancelled and deleted upload");
    //   } catch (err) {
    //     console.error("Failed to delete cancelled upload:", err);
    //   }
    // }
    setShowUploadModal(false);
    setUploadedFileInfo(null);
  };

  const handleFileUpload = async () => {
    if (!uploadFile.length) {
      setUploadError('Please select a file');
      return;
    }

    const file = uploadFile[0];

    // Validate file name - allowing spaces since it's fixed on the backend
    const filenameRegex = /^[0-9a-zA-Z._\- ]+\.mp4$/i;
    if (!filenameRegex.test(file.name)) {
      setUploadError('Invalid filename. Only alphanumeric characters, spaces, dots, underscores, and hyphens are allowed.');
      return;
    }

    // Check file extension
    if (!file.name.toLowerCase().endsWith('.mp4')) {
      setUploadError('Please select a valid MP4 file');
      return;
    }

    try {
      setUploading(true);
      const presignedUrl = await getPresignedUrl(file, meetingType.value);

      const uploadResponse = await fetch(presignedUrl.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'video/mp4'
        },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }
      setUploadedFileInfo(presignedUrl);

      setUploadedFileInfo(presignedUrl);
      setShowConfirmModal(false);
      setShowUploadModal(false);
      setUploadFile([]);
      setUploadError('');

      // Refresh meetings list after successful upload
      fetchMeetings();
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError('Failed to upload file. Please try again.');
      setShowConfirmModal(false);
    } finally {
      setUploading(false);
    }
  };
  const [averageFillerWords, setAverageFillerWords] = useState(0);
  const [averageWeaselWords, setAverageWeaselWords] = useState(0);
  const [averageSpeakingRate, setAverageSpeakingRate] = useState(0);
  const [averageScore, setAverageScore] = useState(0);
  // const [score, setScore] = useState(0);

  const fetchMeetings = async () => {
    if (loading) return;
    try {
      setLoading(true);
      const meetingsData = await getMeetingsFromDynamoDB();
      
      // Log meetings with incomplete data for debugging
      const incompleteMeetings = meetingsData.filter(meeting => isIncompleteData(meeting));
      if (incompleteMeetings.length > 0) {
        console.log(`Found ${incompleteMeetings.length} meetings with incomplete data:`, 
          incompleteMeetings.map(m => ({ uuid: m.uuid, hasCallId: !!m.callId, hasFiller: !!m.filler, hasWeasel: !!m.weasel,hasWpm: !!m.wpm }))
        );
      }
      
      setMeetings(meetingsData);
      setLoading(false);
      calculateAverageFillerWords(meetingsData);
      calculateAverageWeaselWords(meetingsData);
      calculateAverageSpeakingRate(meetingsData);
      calculateAverageScore(meetingsData);
    } catch (error) {
      console.error("Error fetching meetings:", error);
      setLoading(false);
    }
  };

  // Initial fetch of meetings data
  useEffect(() => {
    fetchMeetings();
  }, []);
  
  // Set up a periodic refresh to check for updated meeting data if there are incomplete meetings
  useEffect(() => {
    // Only set up the interval if there are meetings with incomplete data
    const hasIncompleteMeetings = meetings.some(meeting => isIncompleteData(meeting));
    
    if (hasIncompleteMeetings) {
      console.log("Setting up refresh interval for incomplete meetings...");
      const refreshInterval = setInterval(() => {
        console.log("Refreshing meetings data to check for updates...");
        fetchMeetings();
      }, 10000); // Refresh every 10 seconds
      
      // Clean up the interval when the component unmounts or when meetings change
      return () => {
        console.log("Clearing refresh interval");
        clearInterval(refreshInterval);
      };
    }
  }, [meetings]); // Re-run when meetings array changes (including content changes)

  console.log("Current meetings state:", meetings);
  console.log(meetings.length);

        var filteredMeetings = [];
        try {
                filteredMeetings = meetings.filter(meeting =>
                        meeting.uuid && meeting.uuid.toLowerCase().includes(filteringText.toLowerCase())
                ).sort(function (x, y) {
                        // First sort by processing status (incomplete/processing meetings at the top)
                        const xIncomplete = isIncompleteData(x);
                        const yIncomplete = isIncompleteData(y);
                        
                        if (xIncomplete && !yIncomplete) return -1;
                        if (!xIncomplete && yIncomplete) return 1;
                        
                        // Then sort by timestamp (newest first)
                        return y.timestamp - x.timestamp;
                });
        } catch (e) {
                console.log("got error:", e);
        }

  const calculateAverageFillerWords = (meetingsData) => {
    if (meetingsData.length === 0) {
      setAverageFillerWords(0);
      return;
    }

    const totalFillerWords = meetingsData.reduce((sum, meeting) => {
      // Ensure filler is a number, if not, default to 0
      const fillerCount = Number(meeting.filler) || 0;
      return sum + fillerCount;
    }, 0);

    const average = totalFillerWords / meetingsData.length;
    setAverageFillerWords(Math.round(average * 100) / 100); // Round to 2 decimal places
  };

  const calculateAverageWeaselWords = (meetingsData) => {
    if (meetingsData.length === 0) {
      setAverageWeaselWords(0);
      return;
    }

    const totalWeaselWords = meetingsData.reduce((sum, meeting) => {
      // Ensure weasel is a number, if not, default to 0
      const weaselCount = Number(meeting.weasel) || 0;
      return sum + weaselCount;
    }, 0);

    const average = totalWeaselWords / meetingsData.length;
    setAverageWeaselWords(Math.round(average * 100) / 100); // Round to 2 decimal places
  };

  const calculateAverageSpeakingRate = (meetingsData) => {
    if (meetingsData.length === 0) {
      setAverageSpeakingRate(0);
      return;
    }

    const totalWPM = meetingsData.reduce((sum, meeting) => {
      const wpmCount = Number(meeting.wpm) || 0;
      return sum + wpmCount;
    }, 0);

    const average = totalWPM / meetingsData.length;
    setAverageSpeakingRate(Math.round(average * 100) / 100); // Round to 2 decimal places
  };

  const calculateAverageScore = (meetingsData) => {
    if (meetingsData.length === 0) {
      setAverageScore(0);
      return;
    }
    const totalScore = meetingsData.reduce((sum, meeting) => {
      try {
        meeting.promptOutput = JSON.parse(meeting.promptOutput);
        const score = Number(meeting.promptOutput.overall_score);
        console.log(Number(meeting.promptOutput.overall_score));
        return sum + score;
      }
      catch (error) {
        console.log(error);
        return sum;
      }
    }, 0);

    const average = totalScore / meetingsData.length;
    // setAverageScore(Math.round(average * 10) / 10); 
    setAverageScore(Math.round(average));
  };
  
  const handleDeleteMeeting = async (uuid) => {
    if (deleteInProgress) return;
    
    if (window.confirm('Are you sure you want to delete this meeting? This action cannot be undone.')) {
      try {
        setDeleteInProgress(true);
        await deleteMeeting(uuid);
        // Remove the deleted meeting from the state
        setMeetings(meetings.filter(meeting => meeting.uuid !== uuid));
        // Recalculate averages
        const updatedMeetings = meetings.filter(meeting => meeting.uuid !== uuid);
        calculateAverageFillerWords(updatedMeetings);
        calculateAverageWeaselWords(updatedMeetings);
        calculateAverageSpeakingRate(updatedMeetings);
        calculateAverageScore(updatedMeetings);
      } catch (error) {
        console.error('Error deleting meeting:', error);
        alert('Failed to delete meeting. Please try again.');
      } finally {
        setDeleteInProgress(false);
      }
    }
  };





  const columnDefinitions = [
    {
      id: 'callId',
      header: 'Call Name',
      sortingField: 'callId',
      cell: item => {
        if (isIncompleteData(item)) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', height: '30px' }}>
              <LoadingBar tableCellMode={true} />
              <span style={{ marginLeft: '10px', color: '#0073bb', fontStyle: 'italic' }}>Processing meeting...</span>
            </div>
          );
        }
        return <Link to={`/meeting/${item.uuid}`}>{item.callId}</Link>;
      }
    },
    {
      id: 'type',
      header: 'Type',
      cell: item => {
        if (isIncompleteData(item)) {
          return <span style={{ color: '#0073bb', fontStyle: 'italic' }}>Processing...</span>;
        }
        try {
          return item.promptOutput.type;
        } catch {
          return 'N/A';
        }
      },
    },
    {
      id: 'filler',
      header: 'Filler Words',
      cell: item => {
        if (isIncompleteData(item)) {
          return <span style={{ color: '#0073bb', fontStyle: 'italic' }}>Processing...</span>;
        }
        return item.filler;
      },
    },
    {
      id: 'weasel',
      header: 'Weasel Words',
      cell: item => {
        if (isIncompleteData(item)) {
          return <span style={{ color: '#0073bb', fontStyle: 'italic' }}>Processing...</span>;
        }
        return item.weasel;
      },
    },
    {
      id: 'wpm',
      header: 'Words Per Minute',
      cell: item => {
        if (isIncompleteData(item)) {
          return <span style={{ color: '#0073bb', fontStyle: 'italic' }}>Processing...</span>;
        }
        return Number.parseFloat(item.wpm).toFixed();
      },
    },
    {
      id: 'timestamp',
      header: 'Timestamp',
      cell: item => {
        if (isIncompleteData(item)) {
          return <span style={{ color: '#0073bb', fontStyle: 'italic' }}>Processing...</span>;
        }
        return new Date(Number.parseInt(item.timestamp)).toLocaleString();
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: item => {
        if (isIncompleteData(item)) {
          return <span style={{ color: '#0073bb', fontStyle: 'italic' }}>Processing...</span>;
        }
        return (
          <Button 
            variant="link" 
            iconName="remove" 
            onClick={() => handleDeleteMeeting(item.uuid)}
            wrapText={false}
          >
            Delete
          </Button>
        );
      },
    },
  ];
  return (
    <>
      <BreadcrumbGroup
        items={[
          { text: "Home", href: "/" },
          { text: "My Meetings", href: "/meetings" },
        ]}
        ariaLabel="Breadcrumbs"
      />
      <ContentLayout
        defaultPadding
        // headerBackgroundStyle="linear-gradient(135deg, rgb(71, 17, 118) 3%, rgb(131, 57, 157) 44%, rgb(149, 85, 182) 69%, rgb(145, 134, 215) 94%)"
        headerVariant="high-contrast"
        maxContentWidth={2000}
        // disableOverlap
        header={
          <Header variant="h1" description="View and manage your meetings">
            Meetings
          </Header>
        }
      >
        <SpaceBetween size="l">
          <Container>
            <KeyValuePairs
              columns={4}
              items={[
                {
                  label: 'Average Score',
                  value: (
                    <Box variant="awsui-value-small" href="#" ariaLabel="Average Score">
                      {averageScore}/100
                    </Box>
                  ),
                },
                {
                  label: 'Average # Filler Words',
                  value: (
                    <Box variant="awsui-value-small" href="#" ariaLabel="Average Filler">
                      {averageFillerWords}
                    </Box>
                  ),
                },
                {
                  label: 'Average # Weasel Words',
                  value: (
                    <Box variant="awsui-value-small" href="#" ariaLabel="Average Weasel">
                      {averageWeaselWords}
                    </Box>
                  ),
                },
                {
                  label: 'Average Speaking Rate',
                  value: (
                    <Box variant="awsui-value-small" href="#" ariaLabel="Average Rate">
                      {averageSpeakingRate} WPM
                    </Box>
                  ),
                }
              ]}
            />
          </Container>
          <Button
            iconName="upload"
            onClick={() => setShowLegalModal(true)}
          >
            Upload MP4 Recording
          </Button>

          <Modal
            onDismiss={() => setShowLegalModal(false)}
            visible={showLegalModal}
            header="Legal Disclaimer"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="primary"
                    onClick={handleLegalConfirm}
                  >
                    I Accept
                  </Button>
                  <Button 
                    variant='primary'
                    onClick={handleLegalCancel}
                  >
                    Cancel
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
          <Box>
            <SpaceBetween size="m">
              <p>Before we process your recording, please review and accept our legal terms:</p>
              <div>
                <p>By proceeding, you confirm that:</p>
                <ul>
                  <li>You have the necessary rights to this content</li>
                  <li>You consent to its processing according to our terms of service</li>
                  <li>You understand that AWS Eloquence is not responsible for any confidential information contained in your recordings</li>
                  <li>The recording does not contain any sensitive personal information</li>
                </ul>
                <p>Do you agree to these terms?</p>
              </div>
            </SpaceBetween>
          </Box>
        </Modal>

          <Modal
            onDismiss={() => setShowUploadModal(false)}
            visible={showUploadModal}
            header="Upload MP4 Recording"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={() => setShowUploadModal(false)}>Cancel</Button>
                  <Button
                    variant="primary"
                    onClick={validateAndShowConfirm}
                    loading={uploading}
                    disabled={!uploadFile.length || uploading}
                  >
                    Upload
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <SpaceBetween size="l">
              <FormField
                label="Meeting type"
                description="Determines which rubric your call is graded against."
              >
                <Select
                  selectedOption={meetingType}
                  onChange={({ detail }) => setMeetingType(detail.selectedOption)}
                  options={MEETING_TYPE_OPTIONS}
                  disabled={uploading}
                />
              </FormField>
              <FileUpload
                onChange={({ detail }) => {
                  setUploadFile(detail.value);
                  setUploadError('');
                }}
                value={uploadFile || []}
                accept="video/mp4"
                i18nStrings={{
                  uploadButtonText: e => 'Choose file',
                  dropzoneText: e => 'Drop MP4 file to upload',
                  removeFileAriaLabel: e => `Remove file ${e.name}`,
                  errorIconAriaLabel: e => 'Error'
                }}
                constraintText="File must be an MP4 video with a name containing only alphanumeric characters, spaces, dots, underscores, and hyphens"
                errorText={uploadError}
              />
            </SpaceBetween>
          </Modal>
          <Modal
            onDismiss={() => setShowConfirmModal(false)}
            visible={showConfirmModal}
            header="Confirm Upload"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={() => setShowConfirmModal(false)}>Cancel</Button>
                  <Button
                    variant="primary"
                    onClick={handleFileUpload}
                    loading={uploading}
                    disabled={uploading}
                  >
                    Yes, upload this file and begin processing.
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <Box>
              <p>Is this the file you want to upload?</p>
              <p><b>{uploadFile.length > 0 ? uploadFile[0].name : ''}</b></p>
              <p>It will be graded as a <b>{meetingType.label}</b> meeting.</p>
            </Box>
          </Modal>


          <Table
            columnDefinitions={columnDefinitions}
            items={filteredMeetings}
            loading={loading}
            loadingText="Loading meetings"


            variant="half-page"
            empty={
              <Box textAlign="center" color="inherit">
                <b>No meetings</b>
                <Box
                  padding={{ bottom: "s" }}
                  variant="p"
                  color="inherit"
                >
                  No meetings to display.
                </Box>
              </Box>
            }
            header={
              <Header
                defaultPadding
                counter={`(${filteredMeetings.length || 0})`}
              >
                Meetings
              </Header>
            }
            filter={
              <TextFilter
                filteringText={filteringText}
                filteringPlaceholder="Find meetings"
                filteringAriaLabel="Filter meetings"
                onChange={({ detail }) => setFilteringText(detail.filteringText)}
              />

            }
            pagination={
              <Pagination
                currentPageIndex={1}
                pagesCount={1}
              />
            }
          />
        </SpaceBetween>
      </ContentLayout>
    </>
  )
}

function Navigation({ activeHref }) {
  const { logout } = useAuth();
  
  return (
    <SideNavigation
      activeHref={activeHref}
      header={{ text: 'AWS Eloquence', href: '/' }}
      onFollow={
        (event) => {
          if (event.detail.href.includes("/logout")) {
            event.preventDefault();
            logout();
          }
        }
      }
      items={[
        { type: 'link', text: 'Learn More', href: '/learnmore' },
        { type: 'link', text: 'Get Started', href: '/getstarted' },
        { type: 'link', text: 'My Meetings', href: '/meetings' },
        { type: 'link', text: 'Meet the Team', href: '/meettheteam' },
        { type: 'link', text: 'Architecture Diagram', href: '/architecture' },
        { type: 'divider' },
        { type: 'link', text: 'Documentation', href: '/documentation' },
        { type: 'divider' },
        { type: 'link', text: 'Logout', href: "/logout" },
      ]}
    />
  );
}

export default MeetingsPage;

